/**
 * Member Gallery Manager Component
 * Allows MEMBER to manage gallery images through change request system
 * - Request to add new images
 * - Request to delete existing images
 * - Request to update caption/altText of existing images
 */

import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiTrash2,
  FiEdit3,
  FiX,
  FiSave,
  FiAlertCircle,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiUpload,
  FiImage
} from 'react-icons/fi';
import { apiClient } from '../../services/api';
import { Button } from '../ui/Button';
import {
  IMAGE_LIMITS,
  IMAGE_ERROR_MESSAGES,
  canUploadMoreImages,
  getRemainingImageSlots
} from '../../constants/limits';

interface GalleryImage {
  id: string;
  imageUrl: string;
  caption?: string;
  altText?: string;
  displayOrder: number;
}

interface MemberGalleryManagerProps {
  companyId: string;
  existingImages: GalleryImage[];
  onRefresh: () => void;
  maxImages?: number;
}

interface PendingRequest {
  imageId: string;
  type: 'add' | 'delete' | 'update';
  status: 'pending' | 'approved' | 'rejected';
}

const MemberGalleryManager: React.FC<MemberGalleryManagerProps> = ({
  companyId,
  existingImages,
  onRefresh,
  maxImages = IMAGE_LIMITS.GALLERY_MAX // Use constant as default
}) => {
  // State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [loading, setLoading] = useState(false);

  // Calculate if can upload more
  const currentCount = existingImages.length;
  const canAddMore = canUploadMoreImages(currentCount);
  const remainingSlots = getRemainingImageSlots(currentCount);

  // Add image state
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string>('');
  const [newImageCaption, setNewImageCaption] = useState('');
  const [newImageAltText, setNewImageAltText] = useState('');

  // Edit image state
  const [editCaption, setEditCaption] = useState('');
  const [editAltText, setEditAltText] = useState('');

  // Delete reason state
  const [deleteReason, setDeleteReason] = useState('');

  // Pending requests (mock - in real app would fetch from API)
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);

  // Handle file selection for add
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return;
    }

    setNewImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Reset add modal
  const resetAddModal = () => {
    setNewImageFile(null);
    setNewImagePreview('');
    setNewImageCaption('');
    setNewImageAltText('');
    setShowAddModal(false);
  };

  // Reset edit modal
  const resetEditModal = () => {
    setSelectedImage(null);
    setEditCaption('');
    setEditAltText('');
    setShowEditModal(false);
  };

  // Reset delete confirm
  const resetDeleteConfirm = () => {
    setSelectedImage(null);
    setDeleteReason('');
    setShowDeleteConfirm(false);
  };

  // Request to add new image
  const handleRequestAdd = async () => {
    if (!newImageFile) {
      toast.error('กรุณาเลือกรูปภาพ');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', newImageFile);  // ← FIXED: Changed from 'image' to 'file' to match backend
      formData.append('caption', newImageCaption);
      formData.append('altText', newImageAltText);
      formData.append('userNotes', `ขอเพิ่มรูปภาพ: ${newImageCaption || 'ไม่มีคำอธิบาย'}`);

      await apiClient.post(
        `/api/member/companies/${companyId}/images/request-add`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('ส่งคำขอเพิ่มรูปภาพสำเร็จ รอผู้ดูแลระบบอนุมัติ');
      resetAddModal();
      onRefresh(); // Refresh company data
    } catch (error: any) {
      console.error('Failed to request add image:', error);

      // Check for gallery limit error from backend
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'string' && errorDetail.includes('gallery')) {
        toast.error(IMAGE_ERROR_MESSAGES.BACKEND_REJECTION + ` (สูงสุด ${IMAGE_LIMITS.GALLERY_MAX} รูป)`);
      } else {
        toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
      }
    } finally {
      setLoading(false);
    }
  };

  // Request to delete image
  const handleRequestDelete = async () => {
    if (!selectedImage) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('userNotes', deleteReason || 'ขอลบรูปภาพนี้');

      await apiClient.post(
        `/api/member/companies/${companyId}/images/${selectedImage.id}/request-delete`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('ส่งคำขอลบรูปภาพสำเร็จ รอผู้ดูแลระบบอนุมัติ');
      resetDeleteConfirm();
      onRefresh(); // Refresh company data
    } catch (error: any) {
      console.error('Failed to request delete image:', error);
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
    } finally {
      setLoading(false);
    }
  };

  // Request to update image metadata
  const handleRequestUpdate = async () => {
    if (!selectedImage) return;

    // Check if anything changed
    if (
      editCaption === (selectedImage.caption || '') &&
      editAltText === (selectedImage.altText || '')
    ) {
      toast('ไม่มีข้อมูลที่เปลี่ยนแปลง');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('caption', editCaption);
      formData.append('altText', editAltText);
      formData.append('userNotes', 'ขอแก้ไขคำอธิบายภาพ');

      await apiClient.post(
        `/api/member/companies/${companyId}/images/${selectedImage.id}/request-update`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('ส่งคำขอแก้ไขรูปภาพสำเร็จ รอผู้ดูแลระบบอนุมัติ');
      resetEditModal();
      onRefresh(); // Refresh company data
    } catch (error: any) {
      console.error('Failed to request update image:', error);
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (image: GalleryImage) => {
    setSelectedImage(image);
    setEditCaption(image.caption || '');
    setEditAltText(image.altText || '');
    setShowEditModal(true);
  };

  // Open delete confirm
  const openDeleteConfirm = (image: GalleryImage) => {
    setSelectedImage(image);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="space-y-4">
      {/* Header with Counter */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            รูปภาพประกอบ
          </h3>
          {/* Image Counter */}
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-sm text-gray-600">
              จัดการรูปภาพบริษัท สูงสุด {maxImages} รูป
            </span>
            <div className="flex items-center space-x-1">
              <span className={`text-sm font-bold ${currentCount >= maxImages ? 'text-red-600' : 'text-blue-600'}`}>
                {currentCount}/{maxImages}
              </span>
              {currentCount >= maxImages ? (
                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full ml-2">
                  เต็มแล้ว
                </span>
              ) : (
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-2">
                  เหลือ {remainingSlots}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Add Image Button */}
        <Button
          onClick={() => {
            if (!canAddMore) {
              toast.error(IMAGE_ERROR_MESSAGES.GALLERY_NO_SLOTS());
              return;
            }
            setShowAddModal(true);
          }}
          variant={canAddMore ? "default" : "secondary"}
          size="sm"
          leftIcon={<FiUpload className="w-4 h-4" />}
          disabled={!canAddMore}
          className={!canAddMore ? 'opacity-50 cursor-not-allowed' : ''}
          title={!canAddMore ? `คุณมีรูปภาพครบ ${maxImages} รูปแล้ว` : 'ขอเพิ่มรูปภาพใหม่'}
        >
          ขอเพิ่มรูปภาพ
        </Button>
      </div>

      {/* Warning when limit reached */}
      {!canAddMore && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
          <FiAlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">คุณมีรูปภาพครบ {maxImages} รูปแล้ว</span>
            <br />
            กรุณาส่งคำขอลบรูปเก่าก่อนเพิ่มรูปใหม่
          </p>
        </div>
      )}

      {/* Existing Images Grid */}
      {existingImages.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {existingImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="relative group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all"
            >
              {/* Image */}
              <div className="aspect-video bg-gray-100">
                <img
                  src={image.imageUrl}
                  alt={image.altText || `Gallery ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Display Order Badge */}
              <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded">
                #{image.displayOrder + 1}
              </div>

              {/* Action Buttons (Visible on Hover) */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 flex items-center justify-center space-x-2 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => openEditModal(image)}
                  className="p-2 bg-white text-blue-600 rounded-full hover:bg-blue-50 transition-colors"
                  title="แก้ไขคำอธิบาย"
                >
                  <FiEdit3 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => openDeleteConfirm(image)}
                  className="p-2 bg-white text-red-600 rounded-full hover:bg-red-50 transition-colors"
                  title="ขอลบรูป"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Caption */}
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                  <p className="text-white text-xs line-clamp-2">
                    {image.caption}
                  </p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        // Empty State
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FiImage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">ยังไม่มีรูปภาพ</p>
          <p className="text-sm text-gray-500">คลิกปุ่ม "ขอเพิ่มรูปภาพ" เพื่อเริ่มต้น</p>
        </div>
      )}

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
        <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">หมายเหตุ</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>การเพิ่ม แก้ไข หรือลบรูปภาพต้องผ่านการอนุมัติจากผู้ดูแลระบบ</li>
            <li>ติดตามสถานะคำขอได้ที่หน้า "คำขอของฉัน"</li>
          </ul>
        </div>
      </div>

      {/* Add Image Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={resetAddModal}
            />

            {/* Modal */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    ขอเพิ่มรูปภาพใหม่
                  </h3>
                  <button
                    type="button"
                    onClick={resetAddModal}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* File Input */}
                {!newImagePreview ? (
                  <label className="block cursor-pointer">
                    <div className="flex items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors">
                      <div className="text-center">
                        <FiUpload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-700 font-medium mb-1">
                          คลิกเพื่อเลือกรูปภาพ
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, WebP ไม่เกิน 5MB
                        </p>
                      </div>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </label>
                ) : (
                  // Preview
                  <div className="relative mb-4">
                    <img
                      src={newImagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setNewImageFile(null);
                        setNewImagePreview('');
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                    <p className="text-xs text-gray-600 mt-2">
                      {newImageFile?.name} ({(newImageFile!.size / 1024).toFixed(0)} KB)
                    </p>
                  </div>
                )}

                {/* Caption Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    คำอธิบายภาพ (ไม่บังคับ)
                  </label>
                  <textarea
                    value={newImageCaption}
                    onChange={(e) => setNewImageCaption(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="เช่น สำนักงานใหญ่ของบริษัท..."
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {newImageCaption.length}/200 ตัวอักษร
                  </p>
                </div>

                {/* Alt Text Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alt Text สำหรับ SEO (ไม่บังคับ)
                  </label>
                  <input
                    type="text"
                    value={newImageAltText}
                    onChange={(e) => setNewImageAltText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="เช่น สำนักงานบริษัทรักษาความปลอดภัย"
                    maxLength={100}
                  />
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={resetAddModal}
                    disabled={loading}
                    className="flex-1"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleRequestAdd}
                    disabled={loading || !newImageFile}
                    className="flex-1"
                  >
                    {loading ? 'กำลังส่งคำขอ...' : 'ส่งคำขอ'}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Image Modal */}
      <AnimatePresence>
        {showEditModal && selectedImage && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={resetEditModal}
            />

            {/* Modal */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    ขอแก้ไขคำอธิบายภาพ
                  </h3>
                  <button
                    type="button"
                    onClick={resetEditModal}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Image Preview */}
                <div className="mb-4">
                  <img
                    src={selectedImage.imageUrl}
                    alt={selectedImage.altText || 'Gallery'}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>

                {/* Caption Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    คำอธิบายภาพ
                  </label>
                  <textarea
                    value={editCaption}
                    onChange={(e) => setEditCaption(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="เช่น สำนักงานใหญ่ของบริษัท..."
                    maxLength={200}
                  />
                </div>

                {/* Alt Text Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alt Text สำหรับ SEO
                  </label>
                  <input
                    type="text"
                    value={editAltText}
                    onChange={(e) => setEditAltText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="เช่น สำนักงานบริษัทรักษาความปลอดภัย"
                    maxLength={100}
                  />
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={resetEditModal}
                    disabled={loading}
                    className="flex-1"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleRequestUpdate}
                    disabled={loading}
                    className="flex-1"
                    leftIcon={<FiSave className="w-4 h-4" />}
                  >
                    {loading ? 'กำลังส่งคำขอ...' : 'ส่งคำขอ'}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && selectedImage && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={resetDeleteConfirm}
            />

            {/* Modal */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-red-900">
                    ยืนยันการขอลบรูปภาพ
                  </h3>
                  <button
                    type="button"
                    onClick={resetDeleteConfirm}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Image Preview */}
                <div className="mb-4">
                  <img
                    src={selectedImage.imageUrl}
                    alt={selectedImage.altText || 'Gallery'}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>

                {/* Warning */}
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                  <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-semibold mb-1">คำเตือน</p>
                    <p>คุณกำลังขอลบรูปภาพนี้ การขอลบต้องผ่านการอนุมัติจากผู้ดูแลระบบ</p>
                  </div>
                </div>

                {/* Reason Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    เหตุผล (ไม่บังคับ)
                  </label>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="เช่น รูปภาพไม่เกี่ยวข้องกับบริษัท..."
                    maxLength={200}
                  />
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={resetDeleteConfirm}
                    disabled={loading}
                    className="flex-1"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleRequestDelete}
                    disabled={loading}
                    className="flex-1"
                    leftIcon={<FiTrash2 className="w-4 h-4" />}
                  >
                    {loading ? 'กำลังส่งคำขอ...' : 'ส่งคำขอลบ'}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MemberGalleryManager;
